import React, { useMemo } from "react"
import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import {
  Form,
  FormFeedback,
  Label,
  Button,
  Row,
  Col,
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import {
  useCreateInventoryAccessory,
  useEditInventoryAccessory,
} from "hooks/useInventoryAccessory"

const InventoryAccessoryAddEdit = ({
  modal,
  toggleModal,
  isEdit,
  editedData,
}) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateInventoryAccessory()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditInventoryAccessory()

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required").max(255),
    sort_order: Yup.number()
      .nullable()
      .min(0, "Must be 0 or greater")
      .typeError("Must be a number"),
    is_active: Yup.boolean(),
    description: Yup.string().nullable(),
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editedData?.name || "",
      sort_order: editedData?.sort_order ?? "",
      is_active: editedData?.is_active ?? true,
      description: editedData?.description || "",
    },
    validationSchema,
    onSubmit: async values => {
      try {
        const payload = {
          name: values.name.trim(),
          sort_order: values.sort_order === "" ? null : Number(values.sort_order),
          is_active: !!values.is_active,
          description: values.description || null,
        }

        const res = isEdit
          ? await editMutation({ id: editedData.id, ...payload })
          : await createMutation(payload)

        toast.success(
          res?.message ??
            `Accessory ${isEdit ? "updated" : "created"} successfully.`
        )
        toggleModal()
      } catch (err) {
        console.error(err)
        toast.error("Failed to save accessory.")
      }
    },
  })

  const buttonText = useMemo(() => {
    const loading = createLoading || editLoading
    return `${isEdit ? "Save Changes" : "Add Accessory"}${loading ? "..." : ""}`
  }, [isEdit, createLoading, editLoading])

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Inventory Accessory" : "Add Inventory Accessory"}
      size="md"
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <div className="mb-3">
          <Label htmlFor="name">
            Name <span className="text-danger">*</span>
          </Label>
          <InputField
            type="text"
            name="name"
            id="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched.name && !!formik.errors.name}
          />
          {formik.touched.name && formik.errors.name && (
            <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
          )}
        </div>

        <Row>
          <Col md={6}>
            <div className="mb-3">
              <Label htmlFor="sort_order">Sort Order</Label>
              <InputField
                type="number"
                name="sort_order"
                id="sort_order"
                value={formik.values.sort_order}
                onChange={formik.handleChange}
              />
              {formik.touched.sort_order && formik.errors.sort_order && (
                <FormFeedback type="invalid">
                  {formik.errors.sort_order}
                </FormFeedback>
              )}
            </div>
          </Col>
          <Col md={6}>
            <div className="mb-3">
              <Label>Active</Label>
              <div className="form-check form-switch mt-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="is_active"
                  checked={!!formik.values.is_active}
                  onChange={e =>
                    formik.setFieldValue("is_active", e.target.checked)
                  }
                />
                <label className="form-check-label" htmlFor="is_active">
                  {formik.values.is_active ? "Active" : "Inactive"}
                </label>
              </div>
            </div>
          </Col>
        </Row>

        <div className="mb-3">
          <Label htmlFor="description">Description</Label>
          <InputField
            type="textarea"
            name="description"
            id="description"
            rows={2}
            value={formik.values.description}
            onChange={formik.handleChange}
          />
        </div>

        <div className="text-end mt-3">
          <Button
            color="success"
            type="submit"
            disabled={createLoading || editLoading}
          >
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default InventoryAccessoryAddEdit
